/**
 * Video Process Handlers
 *
 * All video-related query handlers for Expert and Tutorial videos
 */

import { handleRequest } from './config'
import { ApiResponse } from '../types'
import { httpsCallable } from 'firebase/functions'
import { functions } from '../../libs/Firebase'
import { ExpertContent } from '../../../../pure-js/types/ExpertVideo'
import { TutorialVideo } from '../../../../pure-js/types/TutorialVideo'
import type {
  GetExpertVideosRequest,
  GetExpertVideosResponse,
  GetTutorialVideosRequest,
  GetTutorialVideosResponse
} from '../types/videos'

// ========================================
// QUERY HANDLERS
// ========================================

/**
 * Get expert content (videos + articles)
 * Optionally filter by language (with automatic SV→EN fallback on backend)
 */
export const getExpertVideos = async (request: GetExpertVideosRequest = {}): Promise<ApiResponse<ExpertContent[]>> => {
  return handleRequest(
    () => Promise.resolve({ data: [], success: true } as ApiResponse<ExpertContent[]>),
    async () => {
      try {
        const callable = httpsCallable<GetExpertVideosRequest, GetExpertVideosResponse>(functions, 'getExpertVideos')
        const result = await callable(request)

        if (!result.data.success) {
          const errorMessage = result.data.error || 'Failed to fetch expert videos from backend'
          throw new Error(errorMessage)
        }

        if (!result.data.videos) {
          return {
            data: [],
            success: true
          }
        }

        return {
          data: result.data.videos,
          success: true
        }
      } catch (error: unknown) {
        // Re-throw so React Query can handle it properly
        throw error
      }
    }
  )
}

/**
 * Get tutorial videos
 * Optionally filter by language (with automatic SV→EN fallback on backend)
 */
export const getTutorialVideos = async (
  request: GetTutorialVideosRequest = {}
): Promise<ApiResponse<TutorialVideo[]>> => {
  return handleRequest(
    () => Promise.resolve({ data: [], success: true } as ApiResponse<TutorialVideo[]>),
    async () => {
      try {
        const callable = httpsCallable<GetTutorialVideosRequest, GetTutorialVideosResponse>(
          functions,
          'getTutorialVideos'
        )
        const result = await callable(request)

        if (!result.data.success) {
          const errorMessage = result.data.error || 'Failed to fetch tutorial videos from backend'
          throw new Error(errorMessage)
        }

        if (!result.data.videos) {
          return {
            data: [],
            success: true
          }
        }

        return {
          data: result.data.videos,
          success: true
        }
      } catch (error: unknown) {
        // Re-throw so React Query can handle it properly
        throw error
      }
    }
  )
}
